import axios from 'axios'

const getHeaders = (sessionId: string): Record<string, string> => {
  return {
    'Content-Type': 'application/json',
    'Accept': 'application/json',
    'x-session-id': sessionId,
  }
}

export interface SearchIndexEntry {
  id: string
  customer: string
  job: string
  content_type: string
  name: string
  path: string
  display_name?: string
  description?: string
  content: string
  keyword_text?: string
  metadata?: Record<string, unknown>
  content_hash?: string
  created_at: number
  updated_at: number
}

export interface SearchResult extends SearchIndexEntry {
  score: number
  match_type?: 'keyword+semantic' | 'keyword_only' | 'semantic_only'
}

export interface ListSearchIndexEntriesParams {
  customer: string
  job?: string
  content_type?: string
  limit?: number
  offset?: number
}

export interface ListSearchIndexEntriesResponse {
  entries: SearchIndexEntry[]
  total: number
}

export interface SearchParams {
  query: string
  customer: string
  job?: string
  content_type?: string
  limit?: number
}

export interface SearchResponse {
  results: SearchResult[]
  count: number
}

export interface IndexStatusResponse {
  id?: string
  customer: string
  job: string
  status: string
  phase?: string
  progress_current?: number
  progress_total?: number
  progress_message?: string
  variable_count?: number
  table_count?: number
  error?: string
  started_at?: number
  completed_at?: number
}

/**
 * List search index entries with pagination and optional filters
 */
export const listSearchIndexEntries = async (
  sessionId: string,
  params: ListSearchIndexEntriesParams
): Promise<ListSearchIndexEntriesResponse> => {
  const response = await axios.get<ListSearchIndexEntriesResponse>(
    '/api/v1/search/entries',
    {
      params: {
        customer: params.customer,
        ...(params.job && { job: params.job }),
        ...(params.content_type && { content_type: params.content_type }),
        ...(params.limit && { limit: params.limit }),
        ...(params.offset !== undefined && { offset: params.offset }),
      },
      headers: getHeaders(sessionId),
    }
  )
  return response.data
}

/**
 * Search job data (hybrid vector + keyword search)
 */
export const searchJobData = async (
  sessionId: string,
  params: SearchParams
): Promise<SearchResponse> => {
  const response = await axios.post<SearchResponse>(
    '/api/v1/search',
    params,
    { headers: getHeaders(sessionId) }
  )
  return response.data
}

/**
 * Get index status for a specific job
 */
export const getIndexStatus = async (
  sessionId: string,
  customer: string,
  job: string
): Promise<IndexStatusResponse> => {
  const response = await axios.get<IndexStatusResponse>(
    `/api/v1/job/${encodeURIComponent(customer)}/${encodeURIComponent(job)}/index`,
    { headers: getHeaders(sessionId) }
  )
  return response.data
}

/**
 * Trigger search indexing for a job
 */
export const triggerIndex = async (
  sessionId: string,
  customer: string,
  job: string
): Promise<{ message: string }> => {
  const response = await axios.post(
    `/api/v1/job/${encodeURIComponent(customer)}/${encodeURIComponent(job)}/index`,
    {},
    { headers: getHeaders(sessionId) }
  )
  return response.data
}
